import React, { useState, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { sanitizeMarkdown } from '@/features/chat/shared';
import { ToolCallStep } from '@/features/chat/shared/hooks/useStreamingChat';
import { RootState } from '../../../../reducers';
import {
  askGroqWithToolsStream,
  ToolCallEvent,
} from '../../../../services/multiFileAskAIService';
import {
  trackAskAIChat,
  type AskAIChatType,
} from '../../shared/chatTrackingActions';
import { triggerFileListRefresh } from '../../../library/librarySlice';
import {
  ENABLED_TOOLS,
  getToolDescription,
  fetchFilesByIds,
} from '../constants';
import type {
  LoadingStage,
  ScreenshotData,
  ConversationMessage,
  Stage,
} from '../types';
import { FileType as GlobalFileType } from '../../../../types';

import useLimits from '../../../plan/hooks/limits';

interface UseAskAIParams {
  selectedFiles: string[];
  currentSessionId: string | null;
  conversationHistory: ConversationMessage[];
  setConversationHistory: React.Dispatch<
    React.SetStateAction<ConversationMessage[]>
  >;
  setCompletion: React.Dispatch<React.SetStateAction<string>>;
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
  setIsLoadingFromHistory: React.Dispatch<React.SetStateAction<boolean>>;
  setAutoSelectedFiles: React.Dispatch<React.SetStateAction<GlobalFileType[]>>;
  saveSessionId: (sessionId: string) => void;
  refreshSessions: () => Promise<void>;
}

interface UseAskAIReturn {
  isLoading: boolean;
  loadingStage: LoadingStage;
  toolCallSteps: ToolCallStep[];
  isSearchingFiles: boolean;
  pendingScreenshots: ScreenshotData[];
  directions: string;
  setDirections: React.Dispatch<React.SetStateAction<string>>;

  runAskAI: (message?: string) => Promise<void>;
  handleEditQuery: (messageIndex: number, newContent: string) => Promise<void>;
  handleRegenerateResponse: (messageIndex: number) => Promise<void>;
  stopGeneration: () => void;
}

export function useAskAI(params: UseAskAIParams): UseAskAIReturn {
  const {
    selectedFiles,
    currentSessionId,
    conversationHistory,
    setConversationHistory,
    setCompletion,
    setStage,
    setIsLoadingFromHistory,
    setAutoSelectedFiles,
    saveSessionId,
    refreshSessions,
  } = params;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const team = useSelector((state: RootState) => state.team.selectedTeam);
  const guardLimits = useLimits();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>(null);
  const [directions, setDirections] = useState('');
  const [toolCallSteps, setToolCallSteps] = useState<ToolCallStep[]>([]);
  const [isSearchingFiles, setIsSearchingFiles] = useState(false);
  const [pendingScreenshots, setPendingScreenshots] = useState<
    ScreenshotData[]
  >([]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingResponseRef = useRef<string>('');
  const pendingScreenshotsRef = useRef<ScreenshotData[]>([]);

  const stopGeneration = useCallback(() => {
    if (!abortControllerRef.current) return;

    abortControllerRef.current.abort();
    abortControllerRef.current = null;

    const partialResponse = streamingResponseRef.current;
    if (partialResponse) {
      const sanitizedCompletion = sanitizeMarkdown(partialResponse);
      setConversationHistory((prev) => [
        ...prev,
        { role: 'assistant', content: sanitizedCompletion },
      ]);
      setCompletion(sanitizedCompletion);
      streamingResponseRef.current = '';
    }

    setIsLoading(false);
    setLoadingStage(null);
    setStage('final');
  }, [setConversationHistory, setCompletion, setStage]);

  /**
   * Core streaming function shared by runAskAI, handleEditQuery, and handleRegenerateResponse.
   */
  const streamResponse = useCallback(
    async (
      question: string,
      opts?: { withToolCallTracking?: boolean },
    ): Promise<void> => {
      if (!team?.teamId) throw new Error('Team ID is required');

      abortControllerRef.current = new AbortController();
      streamingResponseRef.current = '';

      const activeToolCalls: Map<string, ToolCallStep> = new Map();
      let fullResponse = '';

      const { sessionId, selectedFileIds } = await askGroqWithToolsStream(
        team.teamId,
        {
          fileIds: selectedFiles,
          question,
          sessionId: currentSessionId ?? undefined,
          enabledTools: [...ENABLED_TOOLS],
          model: 'kimi-k2',
        },
        (accumulatedText: string) => {
          fullResponse = accumulatedText;
          streamingResponseRef.current = accumulatedText;
          flushSync(() => {
            setCompletion(accumulatedText);
            setStage('final');
            const currentScreenshots = pendingScreenshotsRef.current;
            if (currentScreenshots.length > 0) {
              setPendingScreenshots([...currentScreenshots]);
            }
          });
        },
        {
          signal: abortControllerRef.current.signal,
          ...(opts?.withToolCallTracking && {
            onToolCallStart: (toolCall: ToolCallEvent) => {
              const step: ToolCallStep = {
                id: toolCall.toolId || `tool-${Date.now()}`,
                type: 'tool',
                toolName: toolCall.toolName,
                description: getToolDescription(
                  toolCall.toolName,
                  toolCall.args,
                ),
                status: 'running',
              };
              activeToolCalls.set(step.id, step);
              setToolCallSteps(Array.from(activeToolCalls.values()));
              setIsSearchingFiles(true);
            },
            onToolCallComplete: (toolCall: ToolCallEvent) => {
              const stepId = toolCall.toolId || '';
              const existingStep = activeToolCalls.get(stepId);

              const resultData = toolCall.result as
                | {
                    data?: {
                      screenshot_url?: string;
                      _display_type?: string;
                      file_name?: string;
                      timestamp_formatted?: string;
                    };
                    success?: boolean;
                  }
                | undefined;

              // Extract screenshot data
              if (
                resultData?.data?._display_type === 'image' &&
                resultData?.data?.screenshot_url
              ) {
                const newScreenshot: ScreenshotData = {
                  url: resultData.data.screenshot_url,
                  fileName: resultData.data.file_name,
                  timestamp: resultData.data.timestamp_formatted,
                };
                setPendingScreenshots((prev) => {
                  const updated = [...prev, newScreenshot];
                  pendingScreenshotsRef.current = updated;
                  return updated;
                });
              }

              if (existingStep) {
                existingStep.status = 'completed';
                if (resultData?.data) {
                  existingStep.result = resultData;
                }
                const steps = Array.from(activeToolCalls.values());
                setToolCallSteps(steps);
                if (steps.every((s) => s.status === 'completed')) {
                  setIsSearchingFiles(false);
                }

                // Trigger library refresh for trim/combine
                const toolName = toolCall.toolName?.toLowerCase() || '';
                if (
                  (toolName === 'trim_video' ||
                    toolName === 'trim video' ||
                    toolName === 'combine_videos' ||
                    toolName === 'combine videos') &&
                  resultData?.success
                ) {
                  dispatch(triggerFileListRefresh());
                }
              }
            },
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onFilesReceived: (_fileIds: string[]) => {
              // Auto-selected files shown inline after response
            },
          }),
        },
      );

      // Fetch auto-selected files metadata
      if (selectedFileIds && selectedFileIds.length > 0) {
        fetchFilesByIds(selectedFileIds).then((validFiles) => {
          setAutoSelectedFiles(validFiles);
        });
      }

      // Clean up
      setToolCallSteps([]);
      setIsSearchingFiles(false);
      abortControllerRef.current = null;
      streamingResponseRef.current = '';

      const sanitizedCompletion = sanitizeMarkdown(fullResponse);
      setCompletion(sanitizedCompletion);

      // Save session
      saveSessionId(sessionId);

      // Get captured screenshots
      const capturedScreenshots = pendingScreenshotsRef.current;

      // Add assistant response to history
      setConversationHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: sanitizedCompletion,
          screenshots:
            capturedScreenshots.length > 0 ? capturedScreenshots : undefined,
        },
      ]);

      // Clear pending screenshots
      setPendingScreenshots([]);
      pendingScreenshotsRef.current = [];

      setStage('final');
      await refreshSessions();
    },
    [
      team?.teamId,
      selectedFiles,
      currentSessionId,
      setCompletion,
      setStage,
      setAutoSelectedFiles,
      setConversationHistory,
      saveSessionId,
      refreshSessions,
      dispatch,
    ],
  );

  const handleStreamError = useCallback(
    (error: unknown) => {
      abortControllerRef.current = null;
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request was cancelled by user');
      } else {
        setStage('initial');
        toast.error(
          error instanceof Error
            ? error.message
            : t(
                'drawer.multiAskAI.failedToProcessTranscripts',
                'Failed to process transcripts. Please try again.',
              ),
        );
      }
    },
    [setStage, t],
  );

  const runAskAI = async (message?: string) => {
    setIsLoadingFromHistory(false);

    try {
      await guardLimits('multipleRecordingAskAI', async () => {
        const currentDirections = (message || directions).trim();
        const filesToUse = selectedFiles;

        setDirections('');
        setStage('processing');
        setCompletion('');
        setLoadingStage('response');
        setIsLoading(true);
        setToolCallSteps([]);
        setIsSearchingFiles(false);
        setAutoSelectedFiles([]);
        setPendingScreenshots([]);
        pendingScreenshotsRef.current = [];

        try {
          // Add user message to history
          setConversationHistory((prev) => [
            ...prev,
            { role: 'user', content: currentDirections },
          ]);

          // Track
          const chatType: AskAIChatType =
            filesToUse.length > 0 ? 'multifile' : 'search';
          dispatch(
            trackAskAIChat({
              chatType,
              question: currentDirections,
              fileCount: filesToUse.length,
              sessionId: currentSessionId,
              isNewSession: !currentSessionId,
            }),
          );

          await streamResponse(currentDirections, {
            withToolCallTracking: true,
          });
        } catch (error) {
          console.error('Failed to analyze recordings:', error);
          handleStreamError(error);
        } finally {
          setLoadingStage(null);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.log('Guard limits reached:', error);
      setLoadingStage(null);
      setIsSearchingFiles(false);
      setDirections('');
    }
  };

  const handleEditQuery = async (messageIndex: number, newContent: string) => {
    const userMessage = conversationHistory[messageIndex];
    if (!userMessage || userMessage.role !== 'user') return;

    const truncatedHistory = conversationHistory.slice(0, messageIndex);
    setConversationHistory(truncatedHistory);
    setCompletion('');
    setStage('processing');
    setLoadingStage('response');
    setIsLoading(true);

    try {
      setConversationHistory((prev) => [
        ...prev,
        { role: 'user', content: newContent },
      ]);
      await streamResponse(newContent);
    } catch (error) {
      console.error('Failed to analyze recordings:', error);
      handleStreamError(error);
    } finally {
      setLoadingStage(null);
      setIsLoading(false);
    }
  };

  const handleRegenerateResponse = async (messageIndex: number) => {
    const assistantMessage = conversationHistory[messageIndex];
    if (!assistantMessage || assistantMessage.role !== 'assistant') return;

    const userMessageIndex = messageIndex - 1;
    const userMessage = conversationHistory[userMessageIndex];
    if (!userMessage || userMessage.role !== 'user') return;

    const truncatedHistory = conversationHistory.slice(0, messageIndex);

    flushSync(() => {
      setConversationHistory(truncatedHistory);
      setCompletion('');
      setStage('processing');
      setLoadingStage('response');
      setIsLoading(true);
    });

    try {
      await streamResponse(userMessage.content);
    } catch (error) {
      console.error('Failed to regenerate response:', error);
      handleStreamError(error);
    } finally {
      setLoadingStage(null);
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    loadingStage,
    toolCallSteps,
    isSearchingFiles,
    pendingScreenshots,
    directions,
    setDirections,

    runAskAI,
    handleEditQuery,
    handleRegenerateResponse,
    stopGeneration,
  };
}
